/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const KPIReport: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: { value: string; evidence: File[] } }>({});

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadMetrics();
    }
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (!error && data) {
      setProject(data);
    }
  };

  const loadMetrics = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('project_metrics_progress')
      .select('*')
      .eq('project_id', projectId);

    if (!error && data) {
      setMetrics(data);
      // Initialize form data
      const initialData: { [key: string]: { value: string; evidence: File[] } } = {};
      data.forEach((metric: any) => {
        initialData[metric.id] = { value: metric.current_value?.toString() || '0', evidence: [] };
      });
      setFormData(initialData);
    }
  };

  const handleMetricChange = (metricId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [metricId]: { ...prev[metricId], value }
    }));
  };

  const handleEvidenceChange = (metricId: string, files: FileList | null) => {
    if (files) {
      setFormData(prev => ({
        ...prev,
        [metricId]: { ...prev[metricId], evidence: Array.from(files) }
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !projectId || !ngoId) throw new Error('Not authenticated');

      // Upload evidence files
      const evidenceUrls: { [key: string]: string[] } = {};
      for (const [metricId, data] of Object.entries(formData)) {
        if (data.evidence.length > 0) {
          const urls: string[] = [];
          for (const file of data.evidence) {
            const fileName = `${projectId}/${metricId}/${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage
              .from('kpi-evidence')
              .upload(fileName, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
              .from('kpi-evidence')
              .getPublicUrl(fileName);

            urls.push(publicUrl);
          }
          evidenceUrls[metricId] = urls;
        }
      }

      // Update metrics progress
      for (const [metricId, data] of Object.entries(formData)) {
        const metric = metrics.find((m: any) => m.id === metricId);
        if (metric) {
          await supabase
            .from('project_metrics_progress')
            .update({
              current_value: parseFloat(data.value) || 0,
              updated_at: new Date().toISOString()
            })
            .eq('id', metricId);
        }
      }

      // Create KPI report
      const { error: reportError } = await supabase
        .from('kpi_reports')
        .insert({
          project_id: projectId,
          ngo_id: ngoId,
          report_period: new Date().toISOString().slice(0, 7), // YYYY-MM format
          metrics_data: Object.fromEntries(
            Object.entries(formData).map(([metricId, data]) => [
              metricId,
              { value: parseFloat(data.value) || 0, evidence: evidenceUrls[metricId] || [] }
            ])
          ),
          status: 'submitted',
          submitted_by: user.id,
          submitted_at: new Date().toISOString()
        });

      if (reportError) throw reportError;

      navigate(`/ngo/ngo/progress`);
    } catch (err: any) {
      setError(err.message || 'Failed to submit KPI report');
    } finally {
      setLoading(false);
    }
  };

  if (!project || metrics.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Submit KPI Report</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Report progress for project: <strong>{project.name}</strong>
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {metrics.map((metric: any) => (
            <div key={metric.id} className="card" style={{ marginBottom: '1.5rem' }}>
              <h3>{metric.metric_name}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Target</label>
                  <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
                    {parseFloat(metric.target_value).toLocaleString('en-IN')} {metric.unit || ''}
                  </p>
                </div>
                <div className="form-group">
                  <label>Current Value *</label>
                  <input
                    type="number"
                    value={formData[metric.id]?.value || '0'}
                    onChange={(e) => handleMetricChange(metric.id, e.target.value)}
                    required
                    min="0"
                    step="1"
                    placeholder="Enter achieved value"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Evidence (Photos/PDFs)</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleEvidenceChange(metric.id, e.target.files)}
                />
                <div className="help-text">
                  Upload photos or documents as evidence for this metric
                </div>
                {formData[metric.id]?.evidence && formData[metric.id].evidence.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <strong>Selected files:</strong>
                    <ul style={{ marginTop: '0.25rem', paddingLeft: '1.5rem' }}>
                      {formData[metric.id].evidence.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate(`/ngo/ngo/progress`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit KPI Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KPIReport;


